import { motion } from 'motion/react'
import { useI18n } from '../i18n/useI18n'
import { CATEGORY_GLYPHS, countByCategory } from '../game/items'
import { workstationSurtitleAtLevel } from '../game/workstations'
import type { ItemStack, WorkstationDef } from '../game/types'

type Props = {
  station: WorkstationDef
  inventory: ItemStack[]
  metric?: {
    glyph: string
    value: number
  }
  /** Upgrade tier (1–3); drives the small surtitle above the station name */
  level?: number
  /** When false, station must be built from the detail screen first */
  built?: boolean
  /** When true, plays a pop-in animation and shows a subtle glow */
  isNew?: boolean
  onOpen: (id: WorkstationDef['id']) => void
}

export function WorkstationCard({
  station,
  inventory,
  metric,
  level = 1,
  built = true,
  isNew = false,
  onOpen,
}: Props) {
  const { t, pick } = useI18n()
  const isLockedKind = station.kind === 'locked'
  const isUnbuilt = !built
  const have = station.category ? countByCategory(inventory, station.category) : 0
  const insufficient =
    !isLockedKind && !isUnbuilt && station.category !== undefined && have < 1
  const disabled = isLockedKind

  const resourceGlyph = station.category ? CATEGORY_GLYPHS[station.category] : ''

  return (
    <motion.button
      type="button"
      disabled={disabled}
      onClick={() => onOpen(station.id)}
      initial={isNew ? { scale: 0.82, opacity: 0 } : false}
      animate={{ scale: 1, opacity: 1 }}
      transition={isNew ? { type: 'spring', stiffness: 380, damping: 26 } : undefined}
      className={`flex h-full flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${
        isNew ? 'shadow-[0_0_18px_3px_rgba(251,191,36,0.22)]' : ''
      } ${
        isLockedKind
          ? 'cursor-not-allowed border-stone-800 bg-stone-800/40 opacity-60'
          : isUnbuilt
            ? 'opacity-60 border-dashed border-stone-600 bg-stone-800/45 hover:border-amber-900/80 hover:bg-stone-800/80'
            : insufficient
              ? 'border-stone-800 bg-stone-800/50 text-stone-400 hover:border-amber-900 hover:bg-stone-800'
              : isNew
                ? 'border-amber-700/55 bg-stone-800/70 hover:border-amber-600 hover:bg-stone-800'
                : 'border-stone-800 bg-stone-800/70 hover:border-amber-800 hover:bg-stone-800'
      }`}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <span className="text-2xl leading-none">{isUnbuilt ? '?' : station.glyph}</span>
        <div className="flex flex-col items-end gap-1">
          {!isLockedKind && !isUnbuilt && station.category && (
            <span
              className={`font-mono text-[10px] ${
                insufficient ? 'text-red-500' : 'text-stone-500'
              }`}
            >
              {resourceGlyph} {have}
            </span>
          )}
          {!isLockedKind && !isUnbuilt && metric && (
            <span className="font-mono text-[10px] text-stone-500">
              <span className="text-blue-500 font-bold">{metric.glyph}</span> {metric.value}
            </span>
          )}
        </div>
      </div>
      <div className="min-w-0">
        <div className="mb-0.5 text-[9px] uppercase tracking-[0.18em] text-stone-500">
          {pick(workstationSurtitleAtLevel(station, level))}
        </div>
        <div className="text-sm font-semibold leading-tight text-stone-100">
          {pick(station.name)}
        </div>
      </div>
      {!isUnbuilt && (
        <div className="text-[10px] italic leading-snug text-stone-400">{pick(station.blurb)}</div>
      )}

      {isUnbuilt && (
        <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-500/90">
          {t('workstation_unbuilt')}
        </div>
      )}
      {isLockedKind && (
        <div className="mt-1 text-[10px] uppercase tracking-wider text-stone-600">
          {t('workstation_locked')}
        </div>
      )}
    </motion.button>
  )
}
